// ===== Quick Add prefetch cache (module-scope, shared across all openers) =====
const quickAddCache = new Map();      // url -> raw product-info outerHTML
const quickAddInflight = new Map();   // url -> Promise (in-flight fetch)

function prefetchQuickAdd(url) {
  if (!url) return Promise.resolve(null);
  if (quickAddCache.has(url)) return Promise.resolve(quickAddCache.get(url));
  if (quickAddInflight.has(url)) return quickAddInflight.get(url);

  const promise = fetch(url)
    .then((response) => response.text())
    .then((text) => {
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const el = parsed.querySelector('product-info');
      const html = el ? el.outerHTML : null;
      if (html) quickAddCache.set(url, html);
      quickAddInflight.delete(url);
      return html;
    })
    .catch((err) => {
      quickAddInflight.delete(url);
      throw err;
    });

  quickAddInflight.set(url, promise);
  return promise;
}

function initQuickAddPrefetch(root = document) {
  const buttons = root.querySelectorAll('.quick-add__submit[data-product-url]');
  buttons.forEach((btn) => {
    if (btn.dataset.prefetchBound === '1') return;
    btn.dataset.prefetchBound = '1';

    const url = btn.getAttribute('data-product-url');
    if (!url) return;

    let fired = false;
    const trigger = () => {
      if (fired) return;
      fired = true;
      prefetchQuickAdd(url).catch(() => {});
    };

    const card = btn.closest('.card-wrapper, .product-card-wrapper');
    const host = card || btn;
    host.addEventListener('mouseenter', trigger, { once: true, passive: true });
    host.addEventListener('touchstart', trigger, { once: true, passive: true });
    btn.addEventListener('focus', trigger, { once: true, passive: true });
  });
}

function scheduleQuickAddPrefetchInit() {
  const run = () => initQuickAddPrefetch(document);
  if ('requestIdleCallback' in window) {
    requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 200);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleQuickAddPrefetchInit);
} else {
  scheduleQuickAddPrefetchInit();
}

// Re-scan when new cards stream in (pagination, filter updates, etc.)
document.addEventListener('shopify:section:load', () => initQuickAddPrefetch(document));
new MutationObserver(() => initQuickAddPrefetch(document)).observe(
  document.body,
  { childList: true, subtree: true }
);


if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        const url = opener.getAttribute('data-product-url');
        const spinner = opener.querySelector('.loading__spinner');
        const clearLoading = () => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        };

        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');

        // Cache hit — render instantly, no skeleton flash
        if (url && quickAddCache.has(url)) {
          this.renderProductHTML(quickAddCache.get(url));
          super.show(opener);
          clearLoading();
          return;
        }

        // Cache miss — show skeleton immediately, then fetch in background
        this.renderSkeleton();
        super.show(opener);

        const promise = url ? prefetchQuickAdd(url) : Promise.reject(new Error('no url'));
        promise
          .then((html) => {
            if (!html) throw new Error('no product-info in response');
            // Only swap if the modal is still open for the same opener
            if (this.openedBy !== opener) return;
            this.renderProductHTML(html);
          })
          .catch(() => {
            // Fetch failed — leave skeleton or close? Replace with a soft error.
            if (this.openedBy !== opener) return;
            this.modalContent.innerHTML =
              '<div class="quick-add-skeleton__error">Sorry, this product couldn\'t load. Please try again.</div>';
          })
          .finally(clearLoading);
      }

      renderProductHTML(html) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const productElement = parsed.querySelector('product-info');
        if (!productElement) return;

        this.preprocessHTML(productElement);
        HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
        if (window.ProductModel) window.ProductModel.loadShopifyXR();
      }

      renderSkeleton() {
        this.modalContent.innerHTML = `
          <div class="quick-add-skeleton" aria-hidden="true">
            <div class="quick-add-skeleton__media"></div>
            <div class="quick-add-skeleton__info">
              <div class="quick-add-skeleton__line quick-add-skeleton__line--title"></div>
              <div class="quick-add-skeleton__line quick-add-skeleton__line--price"></div>
              <div class="quick-add-skeleton__line quick-add-skeleton__line--label"></div>
              <div class="quick-add-skeleton__pills">
                <span></span><span></span><span></span><span></span>
              </div>
              <div class="quick-add-skeleton__cta"></div>
            </div>
          </div>
        `;
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        // Strip Judge.me review widgets — they show stale server-rendered placeholder text
        // because Judge.me's JS doesn't re-hydrate inside the modal.
        productElement.querySelectorAll('.jdgm-widget, .jdgm-all-reviews-text, .jdgm-prev-badge, .jdgm-widget-wrapper').forEach((el) => el.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
